import { isEqual, isNull, uniq } from 'lodash/fp';

import { ApplicationState } from '../../../../../store/types';
import { getAIQueriesRoot } from '../../store';
import { AIAgreementType, DocumentQuery, DocumentQueryDB, PortfolioTotal, SystemIdInfo, UpdatedQueryOrder } from './types';

export const getIsFetchingAgreementTypes = (state: ApplicationState): boolean => getAIQueriesRoot(state).isFetchingAgreementTypes;

export const getIsFetchingDocumentQueries = (state: ApplicationState): boolean => getAIQueriesRoot(state).isFetchingQueries;

export const getSelectedAIAgreementTypeId = (state: ApplicationState): number | null => getAIQueriesRoot(state).selectedAgreement;

export const getAllAIAgreementTypes = (state: ApplicationState): AIAgreementType[] => getAIQueriesRoot(state).agreementTypes;

export const getAllAIAgreementTypeIds = (state: ApplicationState): number[] => getAllAIAgreementTypes(state).map(({ agreementTypeId }) => agreementTypeId);

export const getPortfolioTotal = (state: ApplicationState): PortfolioTotal | null => getAIQueriesRoot(state).portfolioTotal;

export const getCurrentDocumentQueries = (state: ApplicationState): DocumentQuery[] => getAIQueriesRoot(state).currentQueries;

export const getSavedDocumentQueries = (state: ApplicationState): DocumentQuery[] => getAIQueriesRoot(state).savedQueries.sort((a, b) => a.queryOrder - b.queryOrder);

export const getUpdatedQueryOrder = (state: ApplicationState): UpdatedQueryOrder[] => {
    const savedQueryOrder = getSavedDocumentQueries(state);
    const currentQueryOrder = getCurrentDocumentQueries(state);
    const updatedQueryOrder: UpdatedQueryOrder[] = currentQueryOrder.map(({ documentQueryId }, index) => ({ documentQueryId: documentQueryId!, queryOrder: index + 1 }));
    const updatedQueries = updatedQueryOrder.reduce((acc: UpdatedQueryOrder[], cur) => {
        const savedQuery = savedQueryOrder.find(({ documentQueryId }) => documentQueryId === cur.documentQueryId)!;
        if (savedQuery.queryOrder !== cur.queryOrder) {
            acc.push(cur);
        }
        return acc;
    }, []);
    return updatedQueries;
};

export const getQueriesUpdated = (state: ApplicationState): boolean => getAIQueriesRoot(state).queriesUpdated;

export const getIsEditingDocumentQueries = (state: ApplicationState): boolean => getAIQueriesRoot(state).isEditing;

export const getUpdatedQueries = (state: ApplicationState): DocumentQuery[] => {
    const currentQueries = getCurrentDocumentQueries(state);
    const savedQueries = getSavedDocumentQueries(state);
    const updatedQueries = currentQueries.reduce((acc: DocumentQuery[], cur) => {
        if (isNull(cur.documentQueryId)) {
            acc.push(cur);
        } else {
            const savedQuery = savedQueries.find(({ documentQueryId }) => documentQueryId === cur.documentQueryId)!;
            if (!isEqual(savedQuery, cur)) {
                acc.push(cur);
            }
        }
        return acc;
    }, []);
    return updatedQueries;
};

export const getDocumentQuerySaveDisabled = (state: ApplicationState): { saveDisabled: boolean, saveDisabledTooltip: string[] } => {
    const queriesUpdated = getQueriesUpdated(state);
    const currentQueries = getCurrentDocumentQueries(state);
    const allQueriesHaveQuestionLength = currentQueries.map(({ question }) => question).every(question => question.length > 0);
    const allSystemIdsHaveLength = currentQueries.map(({ systemId }) => systemId).every(systemId => systemId.length > 0);
    const allDocumentNameIdsHaveLength = currentQueries.map(({ documentNameIds }) => documentNameIds).every(documentNameIds => documentNameIds.length > 0);
    const allKeys = currentQueries.map(({ queryKey }) => queryKey);
    const allKeysHaveLength = allKeys.every(queryKey => queryKey.length > 0);
    const allKeysAreUnique = uniq(allKeys).length === allKeys.length;
    const allQueryKeysHaveLengthAndUnique = allKeysHaveLength && allKeysAreUnique;

    let saveDisabledTooltip: string[] = [];
    if (!allQueriesHaveQuestionLength) {
        saveDisabledTooltip.push('Some of the queries do not have a question');
    }
    if (!allSystemIdsHaveLength) {
        saveDisabledTooltip.push('Some of the queries do not have a system Id');
    }
    if (!allDocumentNameIdsHaveLength) {
        saveDisabledTooltip.push('Some of the queries do not have a document name assigned');
    }
    if (!allQueryKeysHaveLengthAndUnique) {
        saveDisabledTooltip.push('Some of the queries do not have a unique query key');
    }

    const saveDisabled = !queriesUpdated || saveDisabledTooltip.length > 0;

    return { saveDisabled, saveDisabledTooltip };
};

export const getLastUpdatedOrder = (state: ApplicationState): number | null => getAIQueriesRoot(state).lastUpdatedOrder;

export const getAgreementTypeSystemInfo = (state: ApplicationState): SystemIdInfo[] => getAIQueriesRoot(state).agreementTypeSystemInfo;

export const getSelectedClientId = (state: ApplicationState): number | null => getAIQueriesRoot(state).selectedClientId;

export const getDocumentQueryHistory = (state: ApplicationState): DocumentQueryDB[] => getAIQueriesRoot(state).documentQueryHistory;

export const getDocumentQueryHistoryLoading = (state: ApplicationState): boolean => getAIQueriesRoot(state).isFetchingDocumentQueryHistory;

export const getSelectedHistoricalDocumentQueryId = (state: ApplicationState): number | null => getAIQueriesRoot(state).selectedHistoricalDocumentQueryId;
